import { useTranslation } from 'react-i18next';
import { ExportButton } from '../../../components/primitives';
/**
 * AccessLogs — migrated from mock to live API.
 *
 * Reads from: GET /v1/system/audit-logs/access
 * Same AuditLogResponse shape as ActivityLogs but scoped to
 * read-events (sign-in, sign-out, API access, data exports).
 *
 * Filter convention: Action (What) + Search (Who free-text) + chips.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, FormControl,
  IconButton, InputLabel, MenuItem, Paper, Select, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { RefreshOutlined } from '@mui/icons-material';
import { ApiError, systemApi } from '../../../api';
import type { AuditLog } from '../../../api/system';
import { activeLocale } from '../../../utils/datetime';
import { translateApiError } from '../../../i18n/translateApiError';

const ACTION_OPTIONS = ['Sign in', 'Sign out', 'Failed sign in', 'API access', 'Data export'];

function actionColor(a: string): 'success' | 'error' | 'default' | 'warning' {
  const lower = a.toLowerCase();
  if (lower.includes('sign in') && !lower.includes('failed')) return 'success';
  if (lower.includes('failed')) return 'error';
  if (lower.includes('sign out')) return 'default';
  return 'warning';
}

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

const AccessLogs: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionFilter, setActionFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await systemApi.auditApi.listAccess();
      setLogs(data);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('system:accessLogs.loadError'));
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    return logs.filter(d => {
      if (actionFilter !== 'all' && d.action !== actionFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (d.ip_address ?? '').toLowerCase().includes(q)
        || (d.resource_type ?? '').toLowerCase().includes(q)
        || (d.user_agent ?? '').toLowerCase().includes(q)
        || (d.worker_id ?? '').toLowerCase().includes(q)
        || (d.actor_name ?? '').toLowerCase().includes(q)
        || (d.actor_email ?? '').toLowerCase().includes(q);
    });
  }, [logs, actionFilter, search]);

  // The export this page's button PROMISED and never delivered: it rendered with
  // localised copy in all nine locales and NO `onClick`, so every click did nothing
  // (§12.12 — written, wired, rendered, never actually connected). Its sibling
  // ActivityLogs had a working one, which is what made the gap invisible in review.
  //
  // Sign-in history is audit evidence: it is what answers "who got into the account on
  // the 14th". So headers are localised for the person reading it, and the row set is
  // the full filtered list rather than the page.
  const exportColumns = useMemo(() => [
    t('system:accessLogs.tableHeaders.timestamp'),
    t('system:accessLogs.tableHeaders.user'),
    t('system:accessLogs.tableHeaders.action'),
    t('system:accessLogs.tableHeaders.ipAddress'),
    t('system:accessLogs.tableHeaders.device'),
  ], [t]);

  const exportRows = useMemo(() => filtered.map((d) => ({
    [exportColumns[0]]: d.created_at ?? '',
    [exportColumns[1]]: d.actor_name || d.actor_email || d.worker_id || '',
    [exportColumns[2]]: d.action ?? '',
    [exportColumns[3]]: d.ip_address ?? '',
    [exportColumns[4]]: d.user_agent ?? '',
  })), [filtered, exportColumns]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('system:accessLogs.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('system:accessLogs.subtitleDetail')}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={t('system:shared.refresh')}><IconButton size="small" onClick={fetchAll} disabled={loading} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <ExportButton
            filename={`access-logs-${new Date().toISOString().slice(0, 10)}`}
            title={t('system:accessLogs.title')}
            columns={exportColumns}
            rows={exportRows}
          />
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      {/* Filter bar */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t('system:accessLogs.actionLabel')}</InputLabel>
          <Select value={actionFilter} label={t('system:accessLogs.actionLabel')}
            onChange={e => { setActionFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('system:accessLogs.allActions')}</MenuItem>
            {ACTION_OPTIONS.map(a => <MenuItem key={a} value={a}>{t(`system:accessLogs.actions.${a}`)}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label={t('system:accessLogs.searchLabel')}
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          sx={{ width: 300 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
      </Box>

      {/* Chips §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (actionFilter !== 'all')
          chips.push({ key: 'action', label: t('system:accessLogs.actionChip', { value: t(`system:accessLogs.actions.${actionFilter}`, { defaultValue: actionFilter }) }),
            onClear: () => { setActionFilter('all'); setPage(0); } });
        if (search.trim())
          chips.push({ key: 'search', label: t('system:accessLogs.searchChip', { value: search.trim() }),
            onClear: () => { setSearch(''); setPage(0); } });
        if (chips.length === 0) return null;
        return (
          <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {chips.map(c => (
              <Chip key={c.key} label={c.label} size="small" onDelete={c.onClear}
                variant="outlined" color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
            ))}
            <Button size="small"
              onClick={() => { setActionFilter('all'); setSearch(''); setPage(0); }}
              sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary', minWidth: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' } }}>
              {t('system:accessLogs.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {loading ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
              <TableRow>
                <TableCell>{t('system:accessLogs.tableHeaders.timestamp')}</TableCell>
                <TableCell>{t('system:accessLogs.tableHeaders.user')}</TableCell>
                <TableCell align="center">{t('system:accessLogs.tableHeaders.action')}</TableCell>
                <TableCell>{t('system:accessLogs.tableHeaders.ipAddress')}</TableCell>
                <TableCell>{t('system:accessLogs.tableHeaders.device')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {actionFilter !== 'all' || search.trim()
                        ? t('system:accessLogs.noMatch')
                        : t('system:accessLogs.noLogs')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : paginated.map(d => (
                <TableRow key={d.id} hover>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{d.created_at ? new Date(d.created_at).toLocaleString(activeLocale()) : '—'}</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.6rem', bgcolor: actionColor(d.action) === 'error' ? 'error.light' : 'primary.main' }}>{initials(d.actor_name || d.actor_email || d.worker_id)}</Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', lineHeight: 1.2 }}>
                          {d.actor_name || d.actor_email || t('system:accessLogs.unknown')}
                        </Typography>
                        {d.actor_name && d.actor_email && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.2 }}>
                            {d.actor_email}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center"><Chip label={t(`system:accessLogs.actions.${d.action}`, { defaultValue: d.action })} size="small" color={actionColor(d.action)} variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} /></TableCell>
                  <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{d.ip_address ?? '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{d.user_agent ?? '—'}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AccessLogs;
